import { type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

// https://github.com/mantinedev/mantine/blob/master/packages/@mantine/hooks/src/use-debounced-state/use-debounced-state.ts

export function useDebouncedState<T>(defaultValue: T, wait: number, options = { leading: false }) {
    const [value, setValue] = useState(defaultValue);
    const timeoutRef = useRef<number | null>(null);
    const leadingRef = useRef(true);

    const clearTimeout = useCallback((): void => {
        if (typeof timeoutRef.current === 'number') {
            window.clearTimeout(timeoutRef.current);
        }
    }, []);

    useEffect(() => clearTimeout, [clearTimeout]);

    const debouncedSetValue = (newValue: SetStateAction<T>): void => {
        clearTimeout();

        if (leadingRef.current && options.leading) {
            setValue(newValue);
        } else {
            timeoutRef.current = window.setTimeout(() => {
                leadingRef.current = true;
                setValue(newValue);
            }, wait);
        }

        leadingRef.current = false;
    };

    return [value, debouncedSetValue] as const;
}
